import { version } from "../../package.json";
window.APP_VERSION = version;

export const theme = process.env.VUE_APP_THEME || "default";

/**
 * @description URL base de la API. En modo embebido dentro del WebView de la
 *              app mobile (flag `inAppWebView=true` en localStorage), reemplaza
 *              `localhost`/`127.0.0.1` por `10.0.2.2` — desde el emulador
 *              Android, `localhost` es el propio emulador, no el host. La IP
 *              `10.0.2.2` es el alias estándar de AVD para alcanzar el host.
 *              En browser web normal, queda la URL original.
 */
function resolveApiUrl() {
  const raw = process.env.VUE_APP_API_URL || "http://localhost:3000";
  if (
    typeof window !== "undefined" &&
    window.localStorage &&
    localStorage.getItem("inAppWebView") === "true"
  ) {
    return raw
      .replace("localhost", "10.0.2.2")
      .replace("127.0.0.1", "10.0.2.2");
  }
  return raw;
}

export const apiUrl = resolveApiUrl();

export const landingUrl =
  process.env.VUE_APP_LANDING_URL || "http://localhost:5173";

export const shopUrl = "https://gpsfarma.com/ofertas.html";

export const s3Url =
  process.env.VUE_APP_S3_URL || "https://gizhealth-local.s3.amazonaws.com";

/**
 * @description Construye la URL de un archivo almacenado en S3 (o mock local).
 * En producción usa s3Url. En desarrollo local (s3Url default), usa el endpoint
 * /api/local-files del backend que sirve desde storage/s3-local/.
 * @param {string} key - S3 key del archivo (ej: "organizations/x/users/y/avatar.png")
 * @returns {string} URL pública del archivo
 */
export function getFileUrl(key) {
  if (!key) return null;
  const isLocalDev = s3Url === "https://gizhealth-local.s3.amazonaws.com";
  if (isLocalDev) {
    return `${apiUrl}/api/local-files/${encodeURIComponent(key)}`;
  }
  return `${s3Url}/${key}`;
}

export const s3PublicUrl =
  process.env.VUE_APP_S3_PUBLIC_URL ||
  "https://gizhealth-local.s3.amazonaws.com";

/**
 * @description Construye la URL de un asset PÚBLICO (avatar, portada de
 * médico) del bucket público de S3. A diferencia de getFileUrl —que apunta
 * al bucket privado de archivos médicos— este bucket tiene lectura pública,
 * así las imágenes de perfil cargan sin URL firmada.
 * @param {string} key - S3 key del asset
 * @returns {string} URL pública del asset
 */
export function getPublicFileUrl(key) {
  if (!key) return null;
  const isLocalDev = s3PublicUrl === "https://gizhealth-local.s3.amazonaws.com";
  if (isLocalDev) {
    return `${apiUrl}/api/local-files/${encodeURIComponent(key)}`;
  }
  return `${s3PublicUrl}/${key}`;
}

export const audience = process.env.VUE_APP_AUDIENCE || "dashboard";

export const marketingWebsiteUrl =
  process.env.VUE_APP_MARKETING_WEBSITE_URL ||
  "https://gizhealth.com/patient.html";

export const iOSAppStoreUrl =
  process.env.VUE_APP_IOS_APP_STORE_URL || "javascript:;";

export const androidAppStoreUrl =
  process.env.VUE_APP_ANDROID_APP_STORE_URL || "javascript:;";

export const loginRedirectURL =
  process.env.VUE_APP_SCORES_REDIRECT_URL ||
  "https://dashboard.gizhealth.com/login";

export const supportEmail =
  process.env.VUE_APP_SUPPORT_EMAIL || "soporte@quantuxsalud.com";

export const mobileAppName =
  process.env.VUE_APP_MOBILE_APP_NAME || "Consultorio Quantux";

export const organizationId = process.env.VUE_APP_ORGANIZATION_ID || null;

export const enableChat =
  process.env.VUE_APP_ENABLE_CHAT === "true" ? true : false;

export const enableScores =
  process.env.VUE_APP_ENABLE_SCORES === "true" ? true : false;

export const enableEpisodeCreation =
  process.env.VUE_APP_ENABLE_EPISODE_CREATION === "false" ? false : true;

export const isIndicationsEnabled = true;
export const isAppointmentsEnabled = true;
export const isVideoRoomEnabled = true;

export const isPatientEvolutionEnabled =
  process.env.VUE_APP_PATIENT_EVOLUTION_ENABLED === "false" ? false : true;

// This enables Seguimientos
export const isMonitoringEnabled =
  process.env.VUE_APP_MONITORING_ENABLED === "false" ? false : true;

// This enables Protocolos
export const isProtocolsEnabled =
  process.env.VUE_APP_PROTOCOLS_ENABLED === "false" ? false : true;

// This enables Tasks
export const isTasksEnabled =
  process.env.VUE_APP_TASKS_ENABLED === "false" ? false : true;

// This enables programs
export const isProgramsEnabled =
  process.env.VUE_APP_PROGRAMS_ENABLED === "false" ? false : true;

// UC-REG-157: habilita el botón "Comprar" en indicaciones de medicamento
// (PatientHistory + ModalIndication). Federico pidió (2026-07-22) que solo
// QA lo tenga activo por ahora — el resto de los tenants (Ecosistema,
// Vilella, Corp Médica) lo tienen `false` en su .env por tenant. Default
// `true` (no romper QA que no seteó la var explícitamente).
export const isPrescriptionPurchaseEnabled =
  process.env.VUE_APP_PRESCRIPTION_PURCHASE_ENABLED === "false" ? false : true;

const videoRoomEngines = ["twilio", "jitsi"];
export const videoRoomEngine = videoRoomEngines.includes(
  process.env.VUE_APP_VIDEO_ROOM_ENGINE
)
  ? process.env.VUE_APP_VIDEO_ROOM_ENGINE
  : "twilio";

export const isFormPrintingEnabled =
  process.env.VUE_APP_ENABLE_EVENTS_PRINT === "true" ? true : false;

export const isMassiveEnabled =
  process.env.VUE_APP_ENABLE_MASSIVE_ACTIONS === "true" ? true : false;

export const isAppointmentBookingEnabled =
  process.env.VUE_APP_ENABLE_APPOINTMENT_BOOKING === "false" ? false : true;

export const isAgendaVirtualEnabled =
  process.env.VUE_APP_AGENDA_VIRTUAL_ENABLED === "false" ? false : true;
export const isAgendaPricingEnabled =
  process.env.VUE_APP_AGENDA_PRICING_ENABLED === "false" ? false : true;

export const isConsultationOptionsCNCEnabled =
  process.env.VUE_APP_CONSULTATION_OPTIONS_CNC_ENABLED === "true";

export const isHCFormsEnabled =
  process.env.VUE_APP_HC_FORMS_ENABLED === "false" ? false : true;

// UC-REG-258 (Campana, Diego 2026-10-08): oculta la sección "Signos vitales"
// (y su chip rápido) del formulario de indicaciones que usan /prescription y
// el dashboard del paciente. Flag de build por tenant (`.env.<tenant>`),
// misma convención que PROGRAMS/HC_FORMS: default ON, solo "false" apaga.
export const isVitalSignsEnabled =
  process.env.VUE_APP_VITAL_SIGNS_ENABLED === "false" ? false : true;

// UC-REG-260 (Campana, Diego 2026-10-09): oculta el bloque "Adjuntar
// archivos" de todas las secciones del formulario de indicaciones
// (Indications.vue). Flag de build por tenant, misma convención: default ON,
// solo "false" apaga.
export const isIndicationAttachmentsEnabled =
  process.env.VUE_APP_INDICATION_ATTACHMENTS_ENABLED === "false" ? false : true;

// UC-REG-262 (Campana, Diego 2026-10-09): los turnos de trámite (UC-REG-253)
// se asignan por INSTITUCIÓN, no por profesional. Detrás hay un prestador
// ficticio (usuario `VUE_APP_PROCEDURE_APPOINTMENT_DOCTOR_USERNAME`) cuyas
// instituciones son las opciones del select; su nombre no se muestra en los
// turnos de trámite del Dashboard. Default OFF: solo el string "true" activa.
export const isProcedureAppointmentByInstitution =
  process.env.VUE_APP_PROCEDURE_APPOINTMENT_BY_INSTITUTION === "true";

export const procedureAppointmentDoctorUsername =
  process.env.VUE_APP_PROCEDURE_APPOINTMENT_DOCTOR_USERNAME || "";

// UC-REG-295 (Campana, Diego 2026-10-28): modo de la credencial del portal
// del paciente. `member` (default, histórico): credencial de afiliado OOSS
// armada desde GET /members/me (número, plan, token). `municipal`: credencial
// armada desde el User logueado (nombre, apellido, DNI) con la leyenda del
// tenant y el nombre del municipio, más la gestión del grupo familiar
// (`/family-group/me`). Mismas claves que `configs.js` de la app móvil.
// Default `member`: los demás tenants no cambian.
export const credentialMode =
  process.env.VUE_APP_CREDENTIAL_MODE === "municipal" ? "municipal" : "member";
export const isMunicipalCredential = credentialMode === "municipal";
export const credentialBrandName =
  process.env.VUE_APP_CREDENTIAL_BRAND_NAME || "Credencial digital";
export const credentialOrganizationName =
  process.env.VUE_APP_CREDENTIAL_ORGANIZATION_NAME || "";

// UC-REG-296 (Campana, Diego 2026-10-28): "Información comunitaria" —
// farmacias de guardia del día para el paciente y publicación de la nómina
// por el admin (`/pharmacy-duty`). Flag de build por tenant, default OFF:
// solo el string "true" activa.
export const isPharmacyDutyEnabled =
  process.env.VUE_APP_PHARMACY_DUTY_ENABLED === "true";

// UC-REG-298 (Campana, Diego 2026-10-29): la solicitud de trámite pide
// "Médico de preferencia (opcional)" en lugar de la "Descripción *" libre
// (el vecino indica el profesional de cabecera deseado). Flag de build por
// tenant, default OFF: solo el string "true" activa. La API acepta el campo
// siempre; el flag solo decide qué muestra el formulario.
export const isProcedurePreferredDoctorEnabled =
  process.env.VUE_APP_PROCEDURE_PREFERRED_DOCTOR === "true";

// UC-REG-300 (Campana, Diego 2026-10-29): el turno de un trámite se puede
// cancelar desde Turnos / Mis trámites (vecino) y desde la bandeja (staff)
// con `POST /procedures/:id/appointment/cancel`. Flag de build por tenant,
// default OFF: solo el string "true" activa (los demás tenants siguen sin
// botón, como UC-REG-253).
export const isProcedureAppointmentCancelEnabled =
  process.env.VUE_APP_PROCEDURE_APPOINTMENT_CANCEL === "true";

// UC-REG-301 (Campana, Diego 2026-10-29): recetas y órdenes posdatadas.
// Máximo de días que el médico puede diferir la fecha de emisión. Default 0
// = sin campo "Fecha de emisión" (los demás tenants no cambian). Debe
// coincidir con INDICATIONS_POSTDATED_MAX_DAYS de la API del tenant.
const postdatedMaxDaysRaw = Number(
  process.env.VUE_APP_INDICATION_POSTDATED_MAX_DAYS || 0
);
export const indicationPostdatedMaxDays =
  Number.isFinite(postdatedMaxDaysRaw) && postdatedMaxDaysRaw > 0
    ? Math.floor(postdatedMaxDaysRaw)
    : 0;

// UC-REG-303 (Campana, Diego 2026-10-29): el CRM de órdenes muestra también
// las recetas de medicamentos (la API las incluye solo con
// ORDERS_CRM_INCLUDE_MEDICATIONS). Default OFF: solo "true" activa.
export const isOrdersCrmMedicationsEnabled =
  process.env.VUE_APP_ORDERS_CRM_MEDICATIONS === "true";

// UC-REG-303: botón de WhatsApp del CRM de órdenes. Default ON, solo
// "false" lo oculta (tenants sin WhatsApp, como Campana).
export const isOrdersCrmWhatsappEnabled =
  process.env.VUE_APP_ORDERS_CRM_WHATSAPP === "false" ? false : true;

export default {
  theme,
  apiUrl,
  landingUrl,
  shopUrl,
  s3Url,
  audience,
  marketingWebsiteUrl,
  iOSAppStoreUrl,
  androidAppStoreUrl,
  supportEmail,
  mobileAppName,
  organizationId,
  loginRedirectURL,
};
